import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { tolerantId } from "../../utils/firestoreHelpers";
import { bidCanonico } from "../../utils/consultasEdificio";
import Loader from "../../components/Loader";
import ScreenHeader from "../../components/ScreenHeader";
import { FaExclamationTriangle } from "react-icons/fa";
import ResumenIncidencias from "../../components/ResumenIncidencias";
import { colors, shadows, radius, spacing, typography, transitions, cardBase, pageContainer, btnSecondary } from "../../utils/designTokens";

// Ids por consulta de notas: × 2 variantes del edificio ([10, "10"]) = 30
// disyunciones, el tope de Firestore para una consulta con dos `in`.
const INCIDENCIAS_POR_CONSULTA = 15;

const msDe = (ts) =>
  ts?.toMillis ? ts.toMillis() : ts?.toDate ? ts.toDate().getTime() : ts?.seconds ? ts.seconds * 1000 : new Date(ts || 0).getTime() || 0;

/**
 * Última nota interna de cada incidencia (C4): incidentNotesInternal, que solo
 * lee el staff del edificio. Cada consulta fija el edificio (buildingid in) y
 * un grupo de incidencias (incidenciaId in). Devuelve Map incidenciaId → nota.
 * Una incidencia sin buildingid (dato viejo) no se consulta.
 */
async function ultimasNotasInternas(incidencias) {
  const porEdificio = new Map();
  incidencias.forEach((i) => {
    const bid = i.buildingid ?? i.buildingId;
    if (bid == null || String(bid).trim() === "") return;
    const clave = bidCanonico(bid);
    if (!porEdificio.has(clave)) porEdificio.set(clave, { bid, ids: [] });
    porEdificio.get(clave).ids.push(i.id);
  });
  const consultas = [];
  porEdificio.forEach(({ bid, ids }) => {
    for (let k = 0; k < ids.length; k += INCIDENCIAS_POR_CONSULTA) {
      consultas.push(
        getDocs(
          query(
            collection(db, "incidentNotesInternal"),
            where("incidenciaId", "in", ids.slice(k, k + INCIDENCIAS_POR_CONSULTA)),
            where("buildingid", "in", tolerantId(bid))
          )
        )
      );
    }
  });
  const ultimas = new Map();
  (await Promise.all(consultas)).forEach((snap) =>
    snap.docs.forEach((d) => {
      const nota = { id: d.id, ...d.data() };
      const previa = ultimas.get(nota.incidenciaId);
      if (!previa || msDe(nota.createdAt) > msDe(previa.createdAt)) ultimas.set(nota.incidenciaId, nota);
    })
  );
  return ultimas;
}

const IncidenciasUnidad = ({ unidadId, setSelected }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // incidenciaId → última nota interna (incidentNotesInternal).
  const [notasInternas, setNotasInternas] = useState(new Map());

  const fetchIncidencias = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, "incidents"), where("unitId", "==", unidadId));
      const snap = await getDocs(q);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (toDateStr(b.date) || "").localeCompare(toDateStr(a.date) || ""));
      setItems(data);
      // Las notas son un extra: si no se pueden leer (las reglas vigentes no
      // tienen incidentNotesInternal), la columna cae a lastInternalResponse.
      try {
        setNotasInternas(await ultimasNotasInternas(data));
      } catch (e) {
        console.warn("No se pudieron cargar las notas internas:", e?.code || e?.message || e);
        setNotasInternas(new Map());
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!unidadId) return;
    fetchIncidencias();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unidadId]);

  const kpis = useMemo(() => {
    const total = items.length;
    const open = items.filter((i) => i.status === "Reportada" || i.status === "En Proceso" || i.status === "Pendiente").length;
    const closed = items.filter((i) => i.status === "Resuelta").length;
    return { total, open, closed };
  }, [items]);

  return (
    <div style={styles.page}>
      <ScreenHeader
        icon={FaExclamationTriangle}
        title="Incidencias de la Unidad"
        subtitle=""
        onBack={() => setSelected("unidad")}
      />

      {/* KPIs reutilizables */}
      <ResumenIncidencias
        items={items}
        statusMap={{
          reported: ["Reportada", "Pendiente"],
          inProgress: ["En Proceso"],
          resolved: ["Resuelta"],
        }}
      />

      {/* Tabla (cabezales siempre visibles) */}
      <div style={styles.table}>
        <div style={styles.thead}>
          <div>Título</div>
          <div>Fecha</div>
          <div>Prioridad</div>
          <div>Estado</div>
          <div>Descripción</div>
          <div>Nota interna</div>
          <div>Resp. cliente</div>
        </div>

        {/* Body */}
        {loading ? (
          <div style={styles.loadingBox}>
            <Loader />
          </div>
        ) : items.length === 0 ? (
          <div style={styles.emptyRow}>
            <FaExclamationTriangle size={18} />
            <span>No hay incidencias para esta unidad.</span>
          </div>
        ) : (
          items.map((i) => (
            <div key={i.id} style={styles.trow}>
              <div>{i.title || "—"}</div>
              <div>{toDateStr(i.date) || "—"}</div>
              <div style={badgePriority(i.priority)}>{labelPriority(i.priority)}</div>
              <div style={badgeStatus(i.status)}>{labelStatus(i.status)}</div>
              <div>{i.description || "—"}</div>
              {/* Nota interna: la última de incidentNotesInternal (C4). TRANSITORIO:
                  lastInternalResponse cubre las incidencias cuyas notas viejas todavía
                  no se trasladaron; con el traslado (antes de 0D) ese campo desaparece. */}
              <div style={{ fontSize: typography.sizes.sm, color: colors.warningDark, maxWidth: 200 }}>
                {truncate(notasInternas.get(i.id)?.message || i.lastInternalResponse)}
              </div>
              <div style={{ fontSize: typography.sizes.sm, color: colors.successDark, maxWidth: 200 }}>{truncate(i.lastClientResponse || i.lastResponse)}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const truncate = (s, n = 80) => {
  if (!s || typeof s !== "string") return "—";
  const t = s.trim();
  if (!t) return "—";
  return t.length > n ? `${t.slice(0, n)}…` : t;
};

const toDateStr = (d) => {
  if (!d) return "";
  if (typeof d === "string") return d;
  if (typeof d === "number") return new Date(d).toISOString().slice(0, 10);
  if (d?.toDate) return d.toDate().toISOString().slice(0, 10);
  return "";
};

const labelStatus = (s) =>
  s === "Resuelta" ? "Resuelta" : s === "En Proceso" ? "En Proceso" : s === "Pendiente" ? "Pendiente" : "Reportada";

const labelPriority = (p) =>
  p === "high" ? "Alta" : p === "medium" ? "Media" : p === "low" ? "Baja" : "—";

const styles = {
  // Contenedor principal
  page: { ...pageContainer, position: "relative" },
  header: { display: "flex", alignItems: "center", gap: spacing[4], marginBottom: spacing[5] },
  // Botón de volver
  backBtn: {
    ...btnSecondary,
    gap: 6,
    padding: "8px 14px",
    fontSize: typography.sizes.md,
  },
  title: {
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    color: colors.primary,
    margin: 0,
    display: "flex",
    alignItems: "center",
  },

  // KPIs de resumen
  kpis: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: spacing[3],
    marginBottom: spacing[4],
  },
  kpi: {
    borderRadius: radius.md, padding: spacing[3], color: colors.white,
    boxShadow: shadows.md,
    display: "flex", flexDirection: "column", gap: 6,
  },
  kpiLabel: { fontSize: typography.sizes.sm, opacity: 0.9 },
  kpiValue: { fontSize: 20, fontWeight: 400 },
  kpiBlue: { background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryLight})` },
  kpiGreen: { background: `linear-gradient(135deg, ${colors.success}, ${colors.success})` },
  kpiOrange: { background: `linear-gradient(135deg, ${colors.warning}, ${colors.warning})` },

  // Contenedor de tabla
  table: {
    ...cardBase,
    padding: 0,
    overflow: "hidden",
  },
  // Cabecera de tabla
  thead: {
    display: "grid",
    gridTemplateColumns: "200px 100px 120px 120px minmax(140px,1fr) minmax(120px,0.8fr) minmax(120px,0.8fr)",
    gap: 0,
    padding: "12px 16px",
    background: colors.gray50,
    color: colors.gray600,
    fontWeight: 400,
    fontSize: typography.sizes.base,
  },
  // Fila de dato
  trow: {
    display: "grid",
    gridTemplateColumns: "200px 100px 120px 120px minmax(140px,1fr) minmax(120px,0.8fr) minmax(120px,0.8fr)",
    padding: "12px 16px",
    borderTop: `1px solid ${colors.gray200}`,
    fontSize: typography.sizes.md,
    alignItems: "center",
    background: colors.white,
  },

  // Loader dentro de la tabla (debajo de cabecera)
  loadingBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: spacing[6],
    background: colors.white,
  },

  // Fila vacía (cero resultados)
  emptyRow: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    padding: spacing[4],
    borderTop: `1px solid ${colors.gray200}`,
    color: colors.gray700,
    background: colors.white,
  },

  // Badge base para estados y prioridades
  badge: {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: radius.full,
    background: colors.gray200,
    fontSize: typography.sizes.sm,
    color: colors.black,
    textAlign: "center",
  },
};

// Badge de estado de incidencia
const badgeStatus = (s) => {
  if (s === "Resuelta")
    return { ...styles.badge, background: "rgba(39,174,96,0.15)", color: colors.successDark };
  if (s === "En Proceso")
    return { ...styles.badge, background: "rgba(52,152,219,0.15)", color: colors.infoDark };
  return { ...styles.badge, background: "rgba(231,76,60,0.15)", color: colors.errorDark };
};

// Badge de prioridad de incidencia
const badgePriority = (p) => {
  if (p === "high")
    return { ...styles.badge, background: "rgba(231,76,60,0.15)", color: colors.errorDark };
  if (p === "medium")
    return { ...styles.badge, background: "rgba(241,196,15,0.18)", color: colors.warningDark };
  if (p === "low")
    return { ...styles.badge, background: "rgba(39,174,96,0.15)", color: colors.successDark };
  return styles.badge;
};

export default IncidenciasUnidad;
